import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { apiWrite, list } from '../lib/api'
import { useAuth } from '../auth'
import { Empty, PageHead, Skel, Stat } from '../components/ui'
import { money, number, thaiDate } from '../lib/format'

/**
 * International master franchise and licensing (part 135).
 *
 * A territory is a country plus an optional province; an empty province means
 * the whole country. An exclusive grant refuses any other active grant in the
 * same area, a non-exclusive grant only refuses an exclusive one, and two
 * non-exclusive grants may coexist. The server owns that rule; this page shows
 * the refusal verbatim instead of trying to predict it.
 *
 * A royalty is the larger of a progressive band calculation and the quarterly
 * minimum guarantee. Band thresholds are always in baht (satang) even when the
 * agreement trades in another currency: the fixed rate stored on the agreement
 * converts the reported gross once, so the same quarter always prices to the
 * same integer. The browser only previews labels; every payable figure comes
 * back from the API with tiered_satang, minimum_satang and minimum_applied.
 */

type Exclusivity = 'exclusive' | 'non_exclusive'
type LicenceType = 'master' | 'area_development' | 'single_unit'

interface Territory {
  id: string
  name: string
  country_code: string
  province: string
  exclusivity: Exclusivity
  status: 'active' | 'inactive'
  notes: string
  created_at: string
  updated_at: string
}

interface Band {
  from_satang: number
  to_satang: number
  rate_bp: number
}

interface Agreement {
  id: string
  territory_id: string
  territory_name: string
  agreement_no: string
  franchisee_name: string
  licence_type: LicenceType
  currency: string
  currency_minor_units: number
  fx_rate_micros: number
  annual_minimum_satang: number
  bands: Band[]
  start_date: string
  end_date: string
  renewal_notice_days: number
  renewal_term_years: number
  status: string
  term_status?: string
  renewal_deadline?: string
  renewed_end?: string
  created_at: string
  updated_at: string
}

interface Royalty {
  id: string
  agreement_id: string
  agreement_no: string
  franchisee_name: string
  period: string
  currency: string
  currency_minor_units: number
  fx_rate_micros: number
  gross_minor: number
  gross_satang: number
  tiered_satang: number
  minimum_satang: number
  royalty_satang: number
  minimum_applied: boolean
  paid_satang: number
  true_up_satang: number
  computed_at: string
}

interface BandDraft {
  from: string
  to: string
  rate: string
}

const CURRENCIES: Array<{ code: string; label: string; exp: number }> = [
  { code: 'THB', label: 'บาทไทย (THB)', exp: 2 },
  { code: 'USD', label: 'ดอลลาร์สหรัฐ (USD)', exp: 2 },
  { code: 'EUR', label: 'ยูโร (EUR)', exp: 2 },
  { code: 'GBP', label: 'ปอนด์อังกฤษ (GBP)', exp: 2 },
  { code: 'JPY', label: 'เยนญี่ปุ่น (JPY)', exp: 0 },
  { code: 'SGD', label: 'ดอลลาร์สิงคโปร์ (SGD)', exp: 2 },
  { code: 'AUD', label: 'ดอลลาร์ออสเตรเลีย (AUD)', exp: 2 },
  { code: 'CNY', label: 'หยวนจีน (CNY)', exp: 2 },
  { code: 'HKD', label: 'ดอลลาร์ฮ่องกง (HKD)', exp: 2 },
  { code: 'MYR', label: 'ริงกิตมาเลเซีย (MYR)', exp: 2 },
]

const LICENCE_LABELS: Record<LicenceType, string> = {
  master: 'มาสเตอร์แฟรนไชส์',
  area_development: 'พัฒนาพื้นที่',
  single_unit: 'สาขาเดียว',
}

const DEFAULT_BANDS: BandDraft[] = [
  { from: '0', to: '1000000', rate: '5.00' },
  { from: '1000000', to: '5000000', rate: '4.00' },
  { from: '5000000', to: '', rate: '3.00' },
]

function currencyExp(code: string): number {
  return CURRENCIES.find((c) => c.code === code)?.exp ?? 2
}

function currencyLabel(code: string): string {
  return CURRENCIES.find((c) => c.code === code)?.label ?? code
}

/** "2026-11-04", offset in days. */
function isoDay(offsetDays = 0): string {
  const d = new Date()
  d.setDate(d.getDate() + offsetDays)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

/** Five years from today, minus one day: an inclusive five-year term. */
function defaultEnd(): string {
  const d = new Date()
  d.setFullYear(d.getFullYear() + 5)
  d.setDate(d.getDate() - 1)
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

function currentQuarter(): string {
  const d = new Date()
  return `${d.getFullYear()}-Q${Math.floor(d.getMonth() / 3) + 1}`
}

/** Exact decimal string -> integer units, or null. No float in the path. */
function parseUnits(raw: string, decimals: number): number | null {
  const s = raw.trim().replace(/,/g, '')
  if (s === '') return null
  const pattern = decimals === 0 ? /^\d+$/ : new RegExp(`^\\d+(\\.\\d{0,${decimals}})?$`)
  if (!pattern.test(s)) return null
  const [whole, frac = ''] = s.split('.')
  const scale = 10n ** BigInt(decimals)
  const units = BigInt(whole) * scale + BigInt((frac + '0'.repeat(decimals)).slice(0, decimals))
  if (units > BigInt(Number.MAX_SAFE_INTEGER)) return null
  return Number(units)
}

function parseSatang(raw: string): number | null {
  return parseUnits(raw, 2)
}

function parsePercentBP(raw: string): number | null {
  const bp = parseUnits(raw, 2)
  if (bp === null || bp < 0 || bp > 10000) return null
  return bp
}

function percentLabel(bp: number): string {
  const whole = Math.trunc(bp / 100)
  const frac = bp % 100
  if (frac === 0) return `${whole}%`
  if (frac % 10 === 0) return `${whole}.${frac / 10}%`
  return `${whole}.${String(frac).padStart(2, '0')}%`
}

function formatMinor(minor: number, code: string): string {
  const exp = currencyExp(code)
  const value = minor / 10 ** exp
  return `${new Intl.NumberFormat('th-TH', {
    minimumFractionDigits: exp,
    maximumFractionDigits: exp,
  }).format(value)} ${code}`
}

function formatMicros(micros: number): string {
  const whole = Math.trunc(micros / 1_000_000)
  const frac = String(micros % 1_000_000).padStart(6, '0').replace(/0+$/, '')
  return frac === '' ? String(whole) : `${whole}.${frac}`
}

function bandLabel(b: Band): string {
  const from = money(b.from_satang)
  const to = b.to_satang === 0 ? 'ขึ้นไป' : money(b.to_satang)
  return `${from} – ${to} · ${percentLabel(b.rate_bp)}`
}

function termStatus(a: Agreement): { label: string; tone: string } {
  switch (a.term_status) {
    case 'scheduled':
      return { label: `เริ่ม ${thaiDate(a.start_date)}`, tone: 'dim' }
    case 'expired':
      return { label: `สิ้นสุด ${thaiDate(a.end_date)}`, tone: 'sev-critical' }
    case 'renewal_due':
      return {
        label: `ต้องแจ้งต่ออายุภายใน ${thaiDate(a.renewal_deadline ?? a.end_date)}`,
        tone: 'sev-high',
      }
    case 'expiring':
      return { label: `ใกล้สิ้นสุด ${thaiDate(a.end_date)}`, tone: 'sev-medium' }
    default:
      return { label: 'มีผลอยู่', tone: 'sev-low' }
  }
}

export function Franchise() {
  const { me } = useAuth()
  const qc = useQueryClient()
  const role = me?.role
  const canManage = role === 'owner' || role === 'admin' || role === 'audit_manager'
  const canView = canManage || role === 'auditor' || role === 'reviewer'

  const territoriesQ = useQuery({
    queryKey: ['franchise-territories'],
    queryFn: () => list<Territory>('/v1/franchise/territories'),
  })
  const agreementsQ = useQuery({
    queryKey: ['franchise-agreements'],
    queryFn: () => list<Agreement>('/v1/franchise/agreements'),
  })
  const royaltiesQ = useQuery({
    queryKey: ['franchise-royalties'],
    queryFn: () => list<Royalty>('/v1/franchise/royalties'),
  })

  const territories = territoriesQ.data ?? []
  const agreements = agreementsQ.data ?? []
  const royalties = royaltiesQ.data ?? []
  const activeAgreements = agreements.filter((a) => a.status === 'active')

  // ------------------------------------------------------------- territory form
  const [tName, setTName] = useState('')
  const [tCountry, setTCountry] = useState('TH')
  const [tProvince, setTProvince] = useState('')
  const [tExclusivity, setTExclusivity] = useState<Exclusivity>('exclusive')
  const [tNotes, setTNotes] = useState('')
  const [tError, setTError] = useState<string | null>(null)

  const createTerritory = useMutation({
    mutationFn: () =>
      apiWrite<Territory>('/v1/franchise/territories', 'POST', {
        name: tName,
        country_code: tCountry,
        province: tProvince,
        exclusivity: tExclusivity,
        notes: tNotes,
      }),
    onSuccess: (created) => {
      setTName('')
      setTProvince('')
      setTNotes('')
      setTError(null)
      setATerritory(created.id)
      void qc.invalidateQueries({ queryKey: ['franchise-territories'] })
    },
    onError: (e) => setTError(e instanceof Error ? e.message : 'สร้างดินแดนไม่สำเร็จ'),
  })

  function submitTerritory() {
    setTError(null)
    if (tName.trim() === '') {
      setTError('ต้องระบุชื่อดินแดน')
      return
    }
    if (!/^[A-Za-z]{2}$/.test(tCountry.trim())) {
      setTError('รหัสประเทศต้องเป็นตัวอักษรสองตัว เช่น TH, SG, MY')
      return
    }
    createTerritory.mutate()
  }

  // ------------------------------------------------------------- agreement form
  const [aTerritory, setATerritory] = useState('')
  const [aNo, setANo] = useState('')
  const [aFranchisee, setAFranchisee] = useState('')
  const [aLicence, setALicence] = useState<LicenceType>('master')
  const [aCurrency, setACurrency] = useState('THB')
  const [aFX, setAFX] = useState('1.000000')
  const [aMin, setAMin] = useState('4000000.00')
  const [aStart, setAStart] = useState(isoDay())
  const [aEnd, setAEnd] = useState(defaultEnd())
  const [aNotice, setANotice] = useState('90')
  const [aRenewal, setARenewal] = useState('5')
  const [bandDrafts, setBandDrafts] = useState<BandDraft[]>(DEFAULT_BANDS)
  const [aError, setAError] = useState<string | null>(null)

  const selectedTerritory = aTerritory || territories.find((t) => t.status === 'active')?.id || ''

  function changeCurrency(code: string) {
    setACurrency(code)
    setAFX(code === 'THB' ? '1.000000' : '')
  }

  function updateBand(index: number, field: keyof BandDraft, value: string) {
    setBandDrafts((prev) => prev.map((b, i) => (i === index ? { ...b, [field]: value } : b)))
  }

  function addBand() {
    setBandDrafts((prev) => {
      const last = prev[prev.length - 1]
      return [...prev, { from: last?.to ?? '', to: '', rate: '' }]
    })
  }

  function removeBand(index: number) {
    setBandDrafts((prev) => prev.filter((_, i) => i !== index))
  }

  function parseBands(): Band[] | null {
    const out: Band[] = []
    for (let i = 0; i < bandDrafts.length; i += 1) {
      const draft = bandDrafts[i]
      const from = parseSatang(draft.from)
      const to = draft.to.trim() === '' ? 0 : parseSatang(draft.to)
      const rate = parsePercentBP(draft.rate)
      if (from === null || to === null || rate === null) return null
      if (i === 0 && from !== 0) return null
      if (i > 0 && from !== out[i - 1].to_satang) return null
      if (to !== 0 && to <= from) return null
      if (i < bandDrafts.length - 1 && to === 0) return null
      if (i === bandDrafts.length - 1 && to !== 0) return null
      out.push({ from_satang: from, to_satang: to, rate_bp: rate })
    }
    return out.length > 0 ? out : null
  }

  const createAgreement = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiWrite<Agreement>('/v1/franchise/agreements', 'POST', body),
    onSuccess: (created) => {
      setANo('')
      setAFranchisee('')
      setAError(null)
      setATerritory(created.territory_id)
      void qc.invalidateQueries({ queryKey: ['franchise-agreements'] })
    },
    onError: (e) => setAError(e instanceof Error ? e.message : 'สร้างสัญญาไม่สำเร็จ'),
  })

  function submitAgreement() {
    setAError(null)
    if (selectedTerritory === '') {
      setAError('ต้องเลือกดินแดนก่อนออกสัญญา')
      return
    }
    if (!/^[A-Za-z0-9._/-]{1,60}$/.test(aNo.trim())) {
      setAError('เลขที่สัญญาใช้ได้เฉพาะ A-Z, 0-9, จุด, ขีดกลาง, ขีดล่าง และทับ ยาวไม่เกิน 60 ตัวอักษร')
      return
    }
    if (aFranchisee.trim() === '') {
      setAError('ต้องระบุชื่อผู้รับสิทธิ์')
      return
    }
    const fx = aCurrency === 'THB' ? 1_000_000 : parseUnits(aFX, 6)
    if (fx === null || fx <= 0) {
      setAError('อัตราแลกเปลี่ยนต้องมากกว่าศูนย์ และมีทศนิยมไม่เกิน 6 ตำแหน่ง')
      return
    }
    const minimum = parseSatang(aMin)
    if (minimum === null) {
      setAError('ค่าขั้นต่ำรายปีต้องเป็นจำนวนเงินบาทที่ไม่ติดลบ')
      return
    }
    if (aEnd <= aStart) {
      setAError('วันสิ้นสุดต้องหลังวันเริ่มสัญญา')
      return
    }
    const notice = Number.parseInt(aNotice, 10)
    const renewal = Number.parseInt(aRenewal, 10)
    if (!Number.isInteger(notice) || notice < 0 || notice > 365) {
      setAError('จำนวนวันแจ้งต่ออายุต้องอยู่ระหว่าง 0 ถึง 365')
      return
    }
    if (!Number.isInteger(renewal) || renewal < 0 || renewal > 20) {
      setAError('ระยะเวลาต่ออายุต้องอยู่ระหว่าง 0 ถึง 20 ปี')
      return
    }
    const bands = parseBands()
    if (bands === null) {
      setAError('ขั้นบันไดต้องเริ่มที่ 0 ต่อเนื่องกัน ไม่มีช่องว่าง และขั้นสุดท้ายต้องไม่มีขอบบน')
      return
    }
    createAgreement.mutate({
      territory_id: selectedTerritory,
      agreement_no: aNo,
      franchisee_name: aFranchisee,
      licence_type: aLicence,
      currency: aCurrency,
      fx_rate_micros: fx,
      annual_minimum_satang: minimum,
      bands,
      start_date: aStart,
      end_date: aEnd,
      renewal_notice_days: notice,
      renewal_term_years: renewal,
    })
  }

  // --------------------------------------------------------------- royalty form
  const [rAgreement, setRAgreement] = useState('')
  const [rPeriod, setRPeriod] = useState(currentQuarter())
  const [rGross, setRGross] = useState('1000000')
  const [rPaid, setRPaid] = useState('0.00')
  const [rError, setRError] = useState<string | null>(null)
  const [result, setResult] = useState<Royalty | null>(null)

  const selectedAgreementID = rAgreement || activeAgreements[0]?.id || ''
  const selectedAgreement = activeAgreements.find((a) => a.id === selectedAgreementID)
  const selectedCurrency = selectedAgreement?.currency ?? 'THB'
  const selectedExp = currencyExp(selectedCurrency)

  const computeRoyalty = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      apiWrite<Royalty>('/v1/franchise/royalties', 'POST', body),
    onSuccess: (statement) => {
      setResult(statement)
      setRError(null)
      void qc.invalidateQueries({ queryKey: ['franchise-royalties'] })
    },
    onError: (e) => setRError(e instanceof Error ? e.message : 'คำนวณค่าลิขสิทธิ์ไม่สำเร็จ'),
  })

  function submitRoyalty() {
    setRError(null)
    if (selectedAgreementID === '') {
      setRError('ต้องมีสัญญาที่ใช้งานอยู่ก่อนคำนวณค่าลิขสิทธิ์')
      return
    }
    if (!/^\d{4}-Q[1-4]$/.test(rPeriod.trim().toUpperCase())) {
      setRError('ไตรมาสต้องอยู่ในรูปแบบ YYYY-Qn เช่น 2026-Q1')
      return
    }
    const gross = parseUnits(rGross, selectedExp)
    if (gross === null) {
      setRError(`ยอดขายต้องเป็นจำนวน ${selectedCurrency} ที่ไม่ติดลบ และมีทศนิยมไม่เกิน ${selectedExp} ตำแหน่ง`)
      return
    }
    const paid = parseSatang(rPaid)
    if (paid === null) {
      setRError('ยอดที่ชำระแล้วต้องเป็นจำนวนเงินบาทที่ไม่ติดลบ')
      return
    }
    computeRoyalty.mutate({
      agreement_id: selectedAgreementID,
      period: rPeriod.trim().toUpperCase(),
      gross_minor: gross,
      paid_satang: paid,
    })
  }

  // ------------------------------------------------------------------- derived
  const totals = useMemo(() => {
    let royalty = 0
    let trueUpDue = 0
    let minimumApplied = 0
    for (const row of royalties) {
      royalty += row.royalty_satang
      if (row.true_up_satang > 0) trueUpDue += row.true_up_satang
      if (row.minimum_applied) minimumApplied += 1
    }
    return { royalty, trueUpDue, minimumApplied }
  }, [royalties])

  const exclusiveCount = territories.filter((t) => t.exclusivity === 'exclusive').length

  if (!canView) {
    return (
      <>
        <PageHead title="แฟรนไชส์และลิขสิทธิ์ระหว่างประเทศ" sub="ดินแดน สัญญามาสเตอร์ และค่าลิขสิทธิ์รายไตรมาส" />
        <div className="card">
          <Empty title="ไม่มีสิทธิ์เข้าถึง" body="หน้านี้เปิดให้เจ้าของ ผู้ดูแลระบบ ผู้จัดการงานตรวจสอบ ผู้ตรวจสอบ และผู้ทบทวนเท่านั้น" />
        </div>
      </>
    )
  }

  return (
    <>
      <PageHead
        title="แฟรนไชส์และลิขสิทธิ์ระหว่างประเทศ"
        sub="ดินแดนเอกสิทธิ์และไม่เอกสิทธิ์ · ขั้นบันไดค่าลิขสิทธิ์พร้อมค่าขั้นต่ำ · ปรับปรุงยอดรายไตรมาสด้วยอัตราแลกเปลี่ยนคงที่"
        actions={<span className="chip">{number(territories.length)} ดินแดน</span>}
      />

      <div className="row" style={{ gap: 8, flexWrap: 'wrap', marginBottom: 16 }}>
        <span className="chip">{number(exclusiveCount)} ดินแดนเอกสิทธิ์</span>
        <span className="chip">{number(activeAgreements.length)} สัญญาที่ใช้งาน</span>
        <span className="chip">{number(royalties.length)} งบรายไตรมาส</span>
        <span className="chip">ค่าลิขสิทธิ์รวม {money(totals.royalty)}</span>
        <span className="chip">ค้างปรับปรุง {money(totals.trueUpDue)}</span>
        <span className="chip">{number(totals.minimumApplied)} งวดใช้ค่าขั้นต่ำ</span>
      </div>

      <div className="grid-main">
        <div className="stack">
          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>ดินแดนแฟรนไชส์</div>
              <span className="dim" style={{ fontSize: 12 }}>จังหวัดว่างหมายถึงทั้งประเทศ</span>
            </div>

            {territoriesQ.isLoading ? (
              <div className="stack"><Skel h={54} /><Skel h={54} /><Skel h={54} /></div>
            ) : territoriesQ.isError ? (
              <div className="form-error">โหลดดินแดนไม่สำเร็จ — ตรวจสอบว่าเส้นทาง API ถูกติดตั้งแล้ว</div>
            ) : territories.length === 0 ? (
              <Empty
                title="ยังไม่มีดินแดน"
                body="สร้างดินแดนแรกด้วยประเทศและจังหวัด หรือเว้นจังหวัดไว้เพื่อครอบคลุมทั้งประเทศ ดินแดนเอกสิทธิ์จะปฏิเสธดินแดนอื่นที่ทับซ้อน"
              />
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                {territories.map((t) => (
                  <div key={t.id} style={{ border: '1px solid var(--color-hairline)', borderRadius: 14, padding: 14 }}>
                    <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                      <div>
                        <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                          <strong>{t.name}</strong>
                          <span
                            className="chip"
                            style={{
                              color: t.exclusivity === 'exclusive' ? 'var(--color-gold)' : 'var(--color-ivory-muted)',
                              borderColor: t.exclusivity === 'exclusive' ? 'rgba(200,169,106,0.32)' : undefined,
                            }}
                          >
                            {t.exclusivity === 'exclusive' ? 'เอกสิทธิ์' : 'ไม่เอกสิทธิ์'}
                          </span>
                          <span className="chip">{t.status === 'active' ? 'ใช้งาน' : 'ปิดใช้งาน'}</span>
                        </div>
                        <div className="dim" style={{ marginTop: 6, fontSize: 12.5 }}>
                          {t.country_code} · {t.province === '' ? 'ทั้งประเทศ' : t.province}
                        </div>
                        {t.notes ? <div className="muted" style={{ marginTop: 4, fontSize: 12 }}>{t.notes}</div> : null}
                      </div>
                      <span className="mono muted" title={t.id}>{t.id.slice(0, 8)}</span>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>สัญญามาสเตอร์และลิขสิทธิ์</div>
              <span className="dim" style={{ fontSize: 12 }}>ขั้นบันไดคิดบนยอดสะสมในไตรมาส</span>
            </div>

            {agreementsQ.isLoading ? (
              <div className="stack"><Skel h={64} /><Skel h={64} /></div>
            ) : agreementsQ.isError ? (
              <div className="form-error">โหลดสัญญาไม่สำเร็จ</div>
            ) : agreements.length === 0 ? (
              <Empty
                title="ยังไม่มีสัญญา"
                body="เลือกดินแดน กรอกผู้รับสิทธิ์ และกำหนดขั้นบันไดกับค่าขั้นต่ำรายปี ระบบจะคำนวณค่าขั้นต่ำรายไตรมาสเป็นหนึ่งในสี่ของยอดรายปี"
              />
            ) : (
              <div className="stack" style={{ gap: 12 }}>
                {agreements.map((a) => {
                  const status = termStatus(a)
                  return (
                    <div key={a.id} style={{ border: '1px solid var(--color-hairline)', borderRadius: 14, padding: 14 }}>
                      <div className="row" style={{ justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
                        <div>
                          <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                            <strong className="mono">{a.agreement_no}</strong>
                            <span className="chip">{LICENCE_LABELS[a.licence_type] ?? a.licence_type}</span>
                            <span className={`sev ${status.tone}`} style={{ marginLeft: 2 }}>
                              {status.tone !== 'dim' ? <span className="sev-dot" /> : null}
                              {status.label}
                            </span>
                          </div>
                          <div style={{ marginTop: 6, fontSize: 13 }}>{a.franchisee_name}</div>
                          <div className="dim" style={{ marginTop: 3, fontSize: 12 }}>
                            {a.territory_name} · {thaiDate(a.start_date)} ถึง {thaiDate(a.end_date)}
                          </div>
                        </div>
                        <span className="mono muted" title={a.id}>{a.id.slice(0, 8)}</span>
                      </div>

                      <div className="row" style={{ gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
                        <span className="chip gold">{currencyLabel(a.currency)}</span>
                        <span className="chip">ค่าขั้นต่ำรายปี {money(a.annual_minimum_satang)}</span>
                        <span className="chip">แจ้งต่ออายุ {a.renewal_notice_days} วัน</span>
                        <span className="chip">{a.renewal_term_years > 0 ? `ต่ออายุได้ ${a.renewal_term_years} ปี` : 'ไม่มีสิทธิ์ต่ออายุ'}</span>
                        {a.renewed_end ? <span className="chip">ถ้าต่ออายุถึง {thaiDate(a.renewed_end)}</span> : null}
                        {a.currency !== 'THB' ? (
                          <span className="chip mono">1 {a.currency} = {formatMicros(a.fx_rate_micros)} THB</span>
                        ) : null}
                      </div>

                      <div className="row muted" style={{ gap: 8, flexWrap: 'wrap', marginTop: 10, fontSize: 11.5 }}>
                        {a.bands.map((b, i) => (
                          <span key={`${a.id}-band-${i}`} className="mono">{bandLabel(b)}</span>
                        ))}
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </div>

          <div className="card card-pad">
            <div className="row" style={{ justifyContent: 'space-between', marginBottom: 12 }}>
              <div className="card-label" style={{ marginBottom: 0 }}>งบค่าลิขสิทธิ์รายไตรมาส</div>
              <span className="dim" style={{ fontSize: 12 }}>หนึ่งสัญญาต่อหนึ่งไตรมาส</span>
            </div>

            {result ? (
              <div style={{ border: '1px solid rgba(200,169,106,0.35)', borderRadius: 14, padding: 14, marginBottom: 14 }}>
                <div className="row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', gap: 8 }}>
                  <div className="row" style={{ gap: 8, flexWrap: 'wrap' }}>
                    <span className="chip gold">{result.period}</span>
                    <span className="chip mono">{result.agreement_no}</span>
                    <span className="chip">{result.minimum_applied ? 'ใช้ค่าขั้นต่ำ' : 'ใช้ค่าตามขั้นบันได'}</span>
                  </div>
                  <span className="dim" style={{ fontSize: 12 }}>{formatMinor(result.gross_minor, result.currency)} ยอดขาย</span>
                </div>
                <div className="grid-3" style={{ marginTop: 12 }}>
                  <Stat label="ค่าตามขั้นบันได" value={money(result.tiered_satang)} foot="คำนวณแบบขั้นก้าวหน้า" />
                  <Stat label="ค่าขั้นต่ำรายไตรมาส" value={money(result.minimum_satang)} foot="หนึ่งในสี่ของค่าขั้นต่ำรายปี" />
                  <Stat
                    label="ยอดที่ต้องชำระ"
                    value={money(result.royalty_satang)}
                    foot={result.minimum_applied ? 'ค่าขั้นต่ำมากกว่าค่าตามขั้นบันได' : 'ค่าตามขั้นบันไดมากกว่าค่าขั้นต่ำ'}
                  />
                </div>
                <div className="row" style={{ gap: 14, flexWrap: 'wrap', marginTop: 12, fontSize: 12.5 }}>
                  <span>ชำระแล้ว {money(result.paid_satang)}</span>
                  <span style={{ color: result.true_up_satang < 0 ? 'var(--color-emerald-soft)' : 'var(--color-gold)' }}>
                    {result.true_up_satang < 0
                      ? `เครดิตยกไป ${money(-result.true_up_satang)}`
                      : `ปรับปรุงเพิ่ม ${money(result.true_up_satang)}`}
                  </span>
                  <span className="dim">ยอดขายแปลงเป็นเงินบาทที่ {money(result.gross_satang)}</span>
                </div>
              </div>
            ) : null}

            {royaltiesQ.isLoading ? (
              <Skel h={150} />
            ) : royaltiesQ.isError ? (
              <div className="form-error">โหลดงบค่าลิขสิทธิ์ไม่สำเร็จ</div>
            ) : royalties.length === 0 ? (
              <Empty title="ยังไม่มีงบรายไตรมาส" body="เลือกสัญญาและไตรมาส แล้วกดคำนวณ ระบบจะบันทึกงบหนึ่งใบต่อสัญญาต่อไตรมาส" />
            ) : (
              <div className="table-wrap">
                <table className="data">
                  <thead>
                    <tr>
                      <th>ไตรมาส</th>
                      <th>สัญญา</th>
                      <th>ยอดขาย</th>
                      <th>ขั้นบันได</th>
                      <th>ขั้นต่ำ</th>
                      <th>ต้องชำระ</th>
                      <th>ปรับปรุง</th>
                      <th>ที่มา</th>
                    </tr>
                  </thead>
                  <tbody>
                    {royalties.map((row) => (
                      <tr key={row.id} style={{ cursor: 'default' }}>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>{row.period}</td>
                        <td className="mono" style={{ whiteSpace: 'nowrap' }}>{row.agreement_no}</td>
                        <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{formatMinor(row.gross_minor, row.currency)}</td>
                        <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{money(row.tiered_satang)}</td>
                        <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{money(row.minimum_satang)}</td>
                        <td className="num tnum" style={{ whiteSpace: 'nowrap' }}>{money(row.royalty_satang)}</td>
                        <td
                          className="num tnum"
                          style={{ whiteSpace: 'nowrap', color: row.true_up_satang < 0 ? 'var(--color-emerald-soft)' : undefined }}
                        >
                          {money(row.true_up_satang)}
                        </td>
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <span className="chip" style={{ color: row.minimum_applied ? 'var(--color-gold)' : 'var(--color-ivory-muted)' }}>
                            {row.minimum_applied ? 'ค่าขั้นต่ำ' : 'ขั้นบันได'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="stack">
          <div className="card card-pad">
            <div className="card-label">สร้างดินแดน</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
              ดินแดนเอกสิทธิ์ปฏิเสธทุกดินแดนที่ทับซ้อน ส่วนดินแดนไม่เอกสิทธิ์อยู่ร่วมกับดินแดนไม่เอกสิทธิ์อื่นได้
              แต่จะถูกปฏิเสธเมื่อทับซ้อนกับดินแดนเอกสิทธิ์
            </div>
            <form
              style={{ marginTop: 12 }}
              onSubmit={(e) => { e.preventDefault(); if (canManage) submitTerritory() }}
            >
              <div className="field">
                <label htmlFor="fr-t-name">ชื่อดินแดน</label>
                <input id="fr-t-name" className="input" maxLength={120} value={tName}
                  disabled={!canManage} onChange={(e) => setTName(e.target.value)} />
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ width: 110 }}>
                  <label htmlFor="fr-t-country">รหัสประเทศ</label>
                  <input id="fr-t-country" className="input mono" maxLength={2} placeholder="TH" value={tCountry}
                    disabled={!canManage} onChange={(e) => setTCountry(e.target.value.toUpperCase())} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-t-province">จังหวัด (เว้นว่าง = ทั้งประเทศ)</label>
                  <input id="fr-t-province" className="input" maxLength={120} value={tProvince}
                    disabled={!canManage} onChange={(e) => setTProvince(e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="fr-t-exclusivity">ความเป็นเอกสิทธิ์</label>
                <select id="fr-t-exclusivity" className="select" value={tExclusivity}
                  disabled={!canManage} onChange={(e) => setTExclusivity(e.target.value as Exclusivity)}>
                  <option value="exclusive">เอกสิทธิ์</option>
                  <option value="non_exclusive">ไม่เอกสิทธิ์</option>
                </select>
              </div>
              <div className="field">
                <label htmlFor="fr-t-notes">หมายเหตุ</label>
                <textarea id="fr-t-notes" className="input" rows={2} maxLength={500} value={tNotes}
                  disabled={!canManage} onChange={(e) => setTNotes(e.target.value)} />
              </div>
              {tError ? <div className="form-error">{tError}</div> : null}
              {!canManage ? <div className="dim" style={{ marginTop: 12, fontSize: 12 }}>บทบาทของคุณดูข้อมูลได้อย่างเดียว</div> : null}
              <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 14 }}
                disabled={!canManage || createTerritory.isPending}>
                {createTerritory.isPending ? 'กำลังสร้าง…' : 'สร้างดินแดน'}
              </button>
            </form>
          </div>

          <div className="card card-pad">
            <div className="card-label">สร้างสัญญา</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
              ขั้นบันไดกำหนดเป็นเงินบาทเสมอ แม้สัญญาจะใช้สกุลอื่น เพื่อให้พอร์ตหลายสกุลวัดบนมาตราเดียวกัน
              ยอดขายจะถูกแปลงครั้งเดียวด้วยอัตราคงที่ที่บันทึกไว้
            </div>
            <form
              style={{ marginTop: 12 }}
              onSubmit={(e) => { e.preventDefault(); if (canManage) submitAgreement() }}
            >
              <div className="field">
                <label htmlFor="fr-a-territory">ดินแดน</label>
                <select id="fr-a-territory" className="select" value={selectedTerritory}
                  disabled={!canManage} onChange={(e) => setATerritory(e.target.value)}>
                  {territories.filter((t) => t.status === 'active').map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name} · {t.country_code}{t.province ? ` · ${t.province}` : ''}
                    </option>
                  ))}
                </select>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-no">เลขที่สัญญา</label>
                  <input id="fr-a-no" className="input mono" maxLength={60} placeholder="MF-2026-001" value={aNo}
                    disabled={!canManage} onChange={(e) => setANo(e.target.value)} />
                </div>
                <div className="field" style={{ width: 160 }}>
                  <label htmlFor="fr-a-licence">ประเภทสิทธิ์</label>
                  <select id="fr-a-licence" className="select" value={aLicence}
                    disabled={!canManage} onChange={(e) => setALicence(e.target.value as LicenceType)}>
                    <option value="master">มาสเตอร์</option>
                    <option value="area_development">พัฒนาพื้นที่</option>
                    <option value="single_unit">สาขาเดียว</option>
                  </select>
                </div>
              </div>
              <div className="field">
                <label htmlFor="fr-a-franchisee">ผู้รับสิทธิ์</label>
                <input id="fr-a-franchisee" className="input" maxLength={200} value={aFranchisee}
                  disabled={!canManage} onChange={(e) => setAFranchisee(e.target.value)} />
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-currency">สกุลเงิน</label>
                  <select id="fr-a-currency" className="select" value={aCurrency}
                    disabled={!canManage} onChange={(e) => changeCurrency(e.target.value)}>
                    {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.label}</option>)}
                  </select>
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-fx">อัตราคงที่ (บาทต่อ 1 หน่วย)</label>
                  <input id="fr-a-fx" className="input mono" inputMode="decimal" value={aFX}
                    disabled={!canManage || aCurrency === 'THB'}
                    onChange={(e) => setAFX(e.target.value)} />
                </div>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-start">วันเริ่มสัญญา</label>
                  <input id="fr-a-start" className="input" type="date" value={aStart}
                    disabled={!canManage} onChange={(e) => setAStart(e.target.value)} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-end">วันสิ้นสุด</label>
                  <input id="fr-a-end" className="input" type="date" value={aEnd}
                    disabled={!canManage} onChange={(e) => setAEnd(e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="fr-a-min">ค่าขั้นต่ำรายปี (บาท)</label>
                <input id="fr-a-min" className="input mono" inputMode="decimal" value={aMin}
                  disabled={!canManage} onChange={(e) => setAMin(e.target.value)} />
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-notice">แจ้งต่ออายุก่อน (วัน)</label>
                  <input id="fr-a-notice" className="input mono" inputMode="numeric" value={aNotice}
                    disabled={!canManage} onChange={(e) => setANotice(e.target.value)} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-a-renewal">ต่ออายุได้ (ปี)</label>
                  <input id="fr-a-renewal" className="input mono" inputMode="numeric" value={aRenewal}
                    disabled={!canManage} onChange={(e) => setARenewal(e.target.value)} />
                </div>
              </div>

              <div className="field" style={{ marginTop: 16 }}>
                <label>ขั้นบันไดค่าลิขสิทธิ์ (บาท · ถึงเว้นว่าง = ไม่จำกัด)</label>
                {bandDrafts.map((b, i) => (
                  <div className="row" key={`band-${i}`} style={{ gap: 6, alignItems: 'flex-end', marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <input className="input mono" inputMode="decimal" placeholder="จาก" value={b.from}
                        aria-label={`ขอบล่างขั้น ${i + 1}`} disabled={!canManage || i === 0}
                        onChange={(e) => updateBand(i, 'from', e.target.value)} />
                    </div>
                    <div style={{ flex: 1 }}>
                      <input className="input mono" inputMode="decimal" placeholder="ถึง" value={b.to}
                        aria-label={`ขอบบนขั้น ${i + 1}`} disabled={!canManage}
                        onChange={(e) => updateBand(i, 'to', e.target.value)} />
                    </div>
                    <div style={{ width: 78 }}>
                      <input className="input mono" inputMode="decimal" placeholder="%" value={b.rate}
                        aria-label={`อัตราขั้น ${i + 1}`} disabled={!canManage}
                        onChange={(e) => updateBand(i, 'rate', e.target.value)} />
                    </div>
                    <button type="button" className="btn btn-ghost btn-sm" disabled={!canManage || bandDrafts.length === 1}
                      onClick={() => removeBand(i)}>ลบ</button>
                  </div>
                ))}
                <button type="button" className="btn btn-sm" disabled={!canManage} onClick={addBand}
                  style={{ marginTop: 4 }}>เพิ่มขั้นบันได</button>
              </div>

              {aError ? <div className="form-error">{aError}</div> : null}
              <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 14 }}
                disabled={!canManage || createAgreement.isPending || territories.length === 0}>
                {createAgreement.isPending ? 'กำลังสร้าง…' : 'สร้างสัญญา'}
              </button>
            </form>
          </div>

          <div className="card card-pad">
            <div className="card-label">คำนวณค่าลิขสิทธิ์รายไตรมาส</div>
            <div className="dim" style={{ fontSize: 11.5, marginTop: 8, lineHeight: 1.8 }}>
              ยอดขายกรอกในสกุลของสัญญา ระบบแปลงเป็นเงินบาทด้วยอัตราคงที่ แล้วเลือกว่าค่าใดมากกว่าระหว่างขั้นบันไดกับค่าขั้นต่ำ
            </div>
            <form
              style={{ marginTop: 12 }}
              onSubmit={(e) => { e.preventDefault(); if (canManage) submitRoyalty() }}
            >
              <div className="field">
                <label htmlFor="fr-r-agreement">สัญญา</label>
                <select id="fr-r-agreement" className="select" value={selectedAgreementID}
                  disabled={!canManage} onChange={(e) => setRAgreement(e.target.value)}>
                  {activeAgreements.length === 0 ? <option value="">ยังไม่มีสัญญาที่ใช้งาน</option> : null}
                  {activeAgreements.map((a) => (
                    <option key={a.id} value={a.id}>{a.agreement_no} · {a.franchisee_name}</option>
                  ))}
                </select>
              </div>
              <div className="row" style={{ gap: 10 }}>
                <div className="field" style={{ width: 120 }}>
                  <label htmlFor="fr-r-period">ไตรมาส</label>
                  <input id="fr-r-period" className="input mono" placeholder="2026-Q1" value={rPeriod}
                    disabled={!canManage} onChange={(e) => setRPeriod(e.target.value)} />
                </div>
                <div className="field" style={{ flex: 1 }}>
                  <label htmlFor="fr-r-gross">ยอดขาย ({selectedCurrency})</label>
                  <input id="fr-r-gross" className="input mono" inputMode="decimal" value={rGross}
                    disabled={!canManage} onChange={(e) => setRGross(e.target.value)} />
                </div>
              </div>
              <div className="field">
                <label htmlFor="fr-r-paid">ชำระระหว่างไตรมาสแล้ว (บาท)</label>
                <input id="fr-r-paid" className="input mono" inputMode="decimal" value={rPaid}
                  disabled={!canManage} onChange={(e) => setRPaid(e.target.value)} />
              </div>
              {rError ? <div className="form-error">{rError}</div> : null}
              <button type="submit" className="btn btn-primary btn-sm" style={{ marginTop: 14 }}
                disabled={!canManage || computeRoyalty.isPending || activeAgreements.length === 0}>
                {computeRoyalty.isPending ? 'กำลังคำนวณ…' : 'คำนวณและบันทึกงบ'}
              </button>
            </form>
          </div>

          <div className="card card-pad">
            <div className="card-label">กติกาที่ระบบบังคับ</div>
            <ul className="dim" style={{ fontSize: 12, lineHeight: 1.9, margin: '12px 0 0', paddingLeft: 18 }}>
              <li>ดินแดนเอกสิทธิ์ห้ามทับซ้อนกับดินแดนที่ใช้งานอยู่ทุกประเภท</li>
              <li>ดินแดนไม่เอกสิทธิ์ห้ามทับซ้อนกับดินแดนเอกสิทธิ์ แต่อยู่ร่วมกับดินแดนไม่เอกสิทธิ์อื่นได้</li>
              <li>จังหวัดว่างหมายถึงทั้งประเทศ และเทียบแบบไม่สนตัวพิมพ์และช่องว่างซ้ำ</li>
              <li>ค่าขั้นต่ำรายไตรมาสคือหนึ่งในสี่ของรายปี เศษที่เหลือยกไปไตรมาสที่สี่</li>
              <li>ยอดที่ต้องชำระคือค่าที่มากกว่าระหว่างขั้นบันไดกับค่าขั้นต่ำ ไม่ใช่ผลบวก</li>
              <li>อัตราแลกเปลี่ยนคงที่ถูกบันทึกไว้กับงบ ปิดไตรมาสแล้วแก้ไม่ได้</li>
            </ul>
          </div>
        </div>
      </div>
    </>
  )
}
